/**
 * Helpers for working with job definition files.
 */
import {
  constString,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipForAlias,
  opArtifactVersionFile,
  opFileContents,
  opProjectArtifact,
  opRootProject,
} from '@wandb/weave-ui/cg';

import {JsonObject} from '../utilTypes';

// Get the contents of the wandb-job.json file for a specific Job version.
export const getJobDefinitionFileOp = (
  entityName: string,
  projectName: string,
  jobName: string,
  alias: string
) => {
  const project = opRootProject({
    entityName: constString(entityName),
    projectName: constString(projectName),
  });
  const artifact = opProjectArtifact({
    project,
    artifactName: constString(jobName),
  });
  const artifactMembership = opArtifactMembershipForAlias({
    artifact,
    aliasName: constString(alias),
  });
  const artifactVersion = opArtifactMembershipArtifactVersion({
    artifactMembership,
    aliasName: constString(alias),
  });
  const file = opArtifactVersionFile({
    artifactVersion,
    path: constString('wandb-job.json'),
  });
  return opFileContents({file});
};

const getExampleValue = (type: string): any => {
  if (type === 'string') {
    return '';
  }
  if (type === 'boolean') {
    return false;
  }
  if (type === 'number') {
    return 0;
  }
  if (type === 'list') {
    return [];
  }
  if (type === 'none') {
    return null;
  }
  if (type === 'artifactVersion') {
    return 'wandb-artifact://<ENTITY_NAME>/<PROJECT_NAME>/<ARTIFACT_COLLECTION_NAME>:<ALIAS_OR_VERSION>';
  }
  console.error("Don't know how to getExampleValue for " + type);
  return null;
};

export const typedDictToDict = (
  typedDict: any,
  useExampleValue: boolean
): JsonObject => {
  const dict: JsonObject = {};
  const typeMap = typedDict.params.type_map;
  const keys = Object.keys(typeMap);
  for (const key of keys) {
    const type = typeMap[key].wb_type;
    if (type === 'typedDict') {
      dict[key] = typedDictToDict(typeMap[key], useExampleValue);
    } else {
      dict[key] = useExampleValue ? getExampleValue(type) : type;
    }
  }
  return dict;
};

// To improve readability of working with Job files.
export class JobFile {
  spec: JsonObject;

  constructor(filecontents: string) {
    this.spec = JSON.parse(filecontents);
  }

  getInputExample(): JsonObject {
    return typedDictToDict(this.spec.input_types, true);
  }

  // The input_types in wandb-job.json have preserved the original
  // config order, while the raw config values for the run might be
  // being alphabetized by the SDK and sorted by key length somewhere else.
  // Users might have intentionally ordered their config (e.g. by importance)
  // so we attempt to recreate it here.
  // TODO: Recursively ordering dict values?
  orderConfig(config: JsonObject): JsonObject {
    const example = this.getInputExample();
    const ordered: JsonObject = {};
    for (const key of Object.keys(example)) {
      if (key in config) {
        ordered[key] = config[key];
      }
    }
    for (const [key, value] of Object.entries(config)) {
      if (!(key in example)) {
        ordered[key] = value;
      }
    }
    return ordered;
  }
}
